// Integrations (C20, D-146): every integration Voxowa might offer as a logo tile, for owners and admins. Opening one
// goes to its own page (D-156), which shows what it does in Voxowa and what the company needs, and counts the look;
// one that connects is set up and managed there; one not built yet can be requested (what it should do, and a note)
// or, while it is being built, followed with Notify me. Requests decide what gets built; nobody sees how many others
// asked.
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useMember, useSession } from "../app/session";
import { Card, ConfirmButton, Empty, ErrorNote, Loading, Page, useAction, useLoad } from "../app/ui";
import { fmtDate, fmtDateTime, fmtStamp } from "../lib/format";
import {
  activityText,
  CATEGORIES,
  type Category,
  defaultUseCase,
  detailsOf,
  initials,
  type Integration,
  INTEGRATIONS,
  type IntegrationStatus,
  type Market,
  marketFor,
  MARKETS,
  marketsLabel,
  USE_CASES,
  type UseCase,
  visibleIntegrations,
} from "../lib/integrations";
import type { IntegrationConnectionRow, IntegrationRequestRow, IntegrationsState } from "../lib/types";
import { FacebookLeadAds, useFacebookReturn } from "./FacebookLeadAds";
import { LeadWebhook } from "./LeadWebhook";

export function Integrations() {
  const { api } = useSession();
  const state = useLoad(() => api.integrationsState(), [api]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<Category | "all">("all");
  const [market, setMarket] = useState<Market | "all" | null>(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  // Facebook's answer after its login, from an address made before Facebook had its own page: it goes on there.
  const back = useFacebookReturn();
  const shownMarket = market ?? marketFor(state.data?.country);
  const statusOf = (i: Integration) => effectiveStatus(i, state.data);
  const shown = useMemo(
    () => visibleIntegrations(INTEGRATIONS, { query, category, market: shownMarket }, statusOf),
    [query, category, shownMarket, state.data],
  );

  if (back) return <Navigate to={`/integrations/facebook-lead-ads${search}`} replace />;
  return (
    <Page
      title="Integrations"
      sub="Bring leads in, log calls from other phone systems, and send outcomes to the tools you use. Ask for the ones you need: requests decide what we build next."
    >
      <ErrorNote error={state.error} />
      <div className="filters integration-filters">
        <input
          type="search"
          placeholder="Search integrations"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search integrations"
        />
        <div className="segmented small" role="group" aria-label="Market">
          {MARKETS.map((m) => (
            <button
              key={m.key}
              type="button"
              className={shownMarket === m.key ? "on" : ""}
              aria-pressed={shownMarket === m.key}
              onClick={() => setMarket(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="chips integration-chips" role="group" aria-label="Category">
        {[{ key: "all" as const, title: "All" }, ...CATEGORIES].map((c) => (
          <button
            key={c.key}
            type="button"
            className={`chip${category === c.key ? " on" : ""}`}
            aria-pressed={category === c.key}
            onClick={() => setCategory(c.key)}
          >
            {c.title}
          </button>
        ))}
      </div>
      {state.loading && !state.data ? <Loading /> : shown.length === 0
        ? (
          <Card>
            <Empty>No integration matches. Try another search or market.</Empty>
          </Card>
        )
        : CATEGORIES.filter((c) => shown.some((i) => i.category === c.key)).map((c) => (
          <section className="integration-section" key={c.key}>
            <div className="section-title">
              {c.title} <span className="faint">· {c.blurb}</span>
            </div>
            <div className="integration-grid">
              {shown.filter((i) =>
                i.category === c.key
              ).map((i) => (
                <IntegrationTile
                  key={i.key}
                  integration={i}
                  status={statusOf(i)}
                  request={requestOf(i, state.data)}
                  connection={lastConnectionOf(i, state.data)}
                  onOpen={() => navigate(`/integrations/${i.key}`)}
                />
              ))}
            </div>
          </section>
        ))}
    </Page>
  );
}

/**
 * One integration's own page (D-156): its name, logo and status, what it does and what the company needs, and below
 * that its setup (Facebook and MyOperator), or its request. Connected ones show their setup first. Facebook comes back
 * here after its login (main.tsx moves its answer behind the #).
 */
export function IntegrationPage() {
  const { key } = useParams();
  const { api } = useSession();
  const state = useLoad(() => api.integrationsState(), [api]);
  const back = useFacebookReturn();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "activity" ? "activity" : "settings";
  const { busy, run } = useAction();
  // Pausing or resuming from the header reloads the setup below as well.
  const [version, setVersion] = useState(0);
  const integration = INTEGRATIONS.find((i) => i.key === key);
  // Count the look once per visit; a failure here never gets in the owner's way.
  useEffect(() => {
    if (integration) void api.openIntegration(integration.key).catch(() => {});
  }, [api, integration?.key]);
  if (!integration) return <Navigate to="/integrations" replace />;

  const status = effectiveStatus(integration, state.data);
  const request = requestOf(integration, state.data);
  const connection = connectionOf(integration, state.data);
  const details = detailsOf(integration);
  const category = CATEGORIES.find((c) => c.key === integration.category)!;
  const onChanged = () => void state.reload();
  const pauseOrResume = (pause: boolean) =>
    void run(
      () => pause ? api.pauseIntegration(integration.key) : api.resumeIntegration(integration.key),
      pause ? "Paused." : "Resumed.",
    ).then((ok) => {
      if (ok) {
        setVersion((v) => v + 1);
        onChanged();
      }
    });
  const about = (
    <Card title="About">
      <h4>What it does</h4>
      <ul className="integration-does">
        {details.does.map((d) => <li key={d}>{d}</li>)}
      </ul>
      <h4>What you need</h4>
      <p>{details.needs}</p>
    </Card>
  );
  const setup = integration.key === "facebook-lead-ads"
    ? <FacebookLeadAds key={version} back={back} onChanged={onChanged} />
    : integration.webhook
    ? <LeadWebhook key={version} integration={integration} onChanged={onChanged} />
    : (
      <Card>
        {integration.key === "myoperator" && connection
          ? <MyOperatorManage connection={connection} onChanged={onChanged} />
          : integration.key === "myoperator"
          ? <MyOperatorConnect onChanged={onChanged} />
          : status === "connected" && connection
          ? <ConnectionInfo connection={connection} />
          : status === "available"
          ? <p className="muted">Connecting it from here comes with its launch; we'll tell you when it's ready.</p>
          : request
          ? (
            <Requested
              integration={integration}
              request={request}
              building={status === "building"}
              onChanged={onChanged}
            />
          )
          : status === "building"
          ? <NotifyMe integration={integration} onChanged={onChanged} />
          : <RequestForm integration={integration} onChanged={onChanged} />}
      </Card>
    );
  const connected = connection !== null;
  const paused = connection?.status === "paused";

  return (
    <Page
      title={
        <span className="integration-page-title">
          <IntegrationLogo integration={integration} size={44} />
          {integration.name}
        </span>
      }
      sub={`${category.title} · ${marketsLabel(integration.markets)}`}
      actions={
        <>
          {paused
            ? (
              <button type="button" className="primary small" disabled={busy} onClick={() => pauseOrResume(false)}>
                Resume
              </button>
            )
            : connected && (
              <ConfirmButton
                label="Pause"
                confirm="Pause it? What arrives meanwhile waits until you resume."
                disabled={busy}
                onConfirm={() => pauseOrResume(true)}
              />
            )}
          <Link to="/integrations">All integrations</Link>
        </>
      }
    >
      <div className="stack integration-page">
        <ErrorNote error={state.error} />
        <div className="row between integration-page-bar">
          <StatusBadge status={status} request={request} connection={lastConnectionOf(integration, state.data)} />
          <div className="segmented small" role="tablist" aria-label="Integration">
            {([["settings", "Settings"], ["activity", "Activity"]] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                className={tab === value ? "on" : ""}
                onClick={() => setParams(value === "settings" ? {} : { tab: value }, { replace: true })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {tab === "activity" ? <IntegrationActivity integration={integration} /> : (
          <>
            {paused && (
              <p className="paused-note">
                Paused: Voxowa brings nothing new in until you resume. What arrives meanwhile is kept and comes in then.
              </p>
            )}
            {connected ? setup : about}
            {connected ? about : setup}
          </>
        )}
      </div>
    </Page>
  );
}

/**
 * Every change to the integration (D-157), newest first: when, to the second, with the offset from GMT; who (a
 * person, or Voxowa when it saw a problem); and what. The company's record of what happened, and ours.
 */
function IntegrationActivity({ integration }: { integration: Integration }) {
  const { api, tz } = useMember();
  const log = useLoad(() => api.integrationActivity(integration.key), [api, integration.key]);
  return (
    <Card title="Activity" flush>
      <ErrorNote error={log.error} />
      {log.loading && !log.data
        ? <Loading />
        : !log.data?.length
        ? <Empty>Nothing has happened here yet.</Empty>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                </tr>
              </thead>
              <tbody>
                {log.data.map((item, i) => (
                  <tr key={`${item.at}-${i}`}>
                    <td className="nowrap">{fmtStamp(item.at, tz)}</td>
                    <td className={item.kind === "member" ? "" : "faint"}>{item.who}</td>
                    <td>{activityText(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Card>
  );
}

/** "Wed, 7 Oct" in the company's time zone. */
function useDay(): (iso: string) => string {
  const { tz } = useMember();
  return (iso) => fmtDate(iso, tz);
}

function requestOf(i: Integration, s: IntegrationsState | null): IntegrationRequestRow | null {
  return s?.requests.find((r) => r.key === i.key) ?? null;
}

function connectionOf(i: Integration, s: IntegrationsState | null): IntegrationConnectionRow | null {
  return s?.connections.find((c) => c.key === i.key && c.status !== "disconnected") ?? null;
}

/** The company's connection as it stands, a stopped one too: what the badge says (D-157). */
function lastConnectionOf(i: Integration, s: IntegrationsState | null): IntegrationConnectionRow | null {
  return s?.connections.find((c) => c.key === i.key) ?? null;
}

/** A company's own connection outranks the catalogue's status. */
function effectiveStatus(i: Integration, s: IntegrationsState | null): IntegrationStatus {
  return connectionOf(i, s) ? "connected" : i.status;
}

/** The logo in its white box, or the name's initials when the file is missing. */
export function IntegrationLogo({ integration, size = 40 }: { integration: Integration; size?: number }) {
  const [missing, setMissing] = useState(false);
  return (
    <span className="integration-logo" style={{ width: size + 16, height: size + 16 }} aria-hidden>
      {missing ? <span className="integration-initials">{initials(integration.name)}</span> : (
        <img
          src={integration.logo}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          onError={() => {
            setMissing(true);
            console.warn(`Integration logo missing: ${integration.key}`);
          }}
        />
      )}
    </span>
  );
}

function StatusBadge(
  { status, request, connection }: {
    status: IntegrationStatus;
    request: IntegrationRequestRow | null;
    connection?: IntegrationConnectionRow | null;
  },
) {
  const day = useDay();
  // A connection's own state first (D-157): connected, waiting for its first event, paused, needing attention, or
  // stopped (it was connected once). One that can be connected but is not says nothing.
  switch (connection?.status) {
    case "connected":
      return <span className="badge ok">Connected</span>;
    case "error":
      return <span className="badge danger">Needs attention</span>;
    case "paused":
      return <span className="badge warm">Paused</span>;
    case "waiting":
      return <span className="badge brand">Waiting for the first lead</span>;
    case "disconnected":
      if (connection.connected_at) return <span className="badge">Stopped</span>;
  }
  if (status === "building") return <span className="badge warm">{request ? "✓ " : ""}Coming soon</span>;
  if (request) return <span className="badge brand">✓ Requested {day(request.requested_at)}</span>;
  return null;
}

function IntegrationTile(
  { integration, status, request, connection, onOpen }: {
    integration: Integration;
    status: IntegrationStatus;
    request: IntegrationRequestRow | null;
    connection: IntegrationConnectionRow | null;
    onOpen: () => void;
  },
) {
  return (
    <article className="integration-tile" onClick={onOpen}>
      <div className="integration-tile-head">
        <IntegrationLogo integration={integration} />
        <StatusBadge status={status} request={request} connection={connection} />
      </div>
      <h3>{integration.name}</h3>
      <p className="muted">{integration.description}</p>
      <button
        type="button"
        className="small"
        aria-label={`Open ${integration.name}`}
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        Open
      </button>
    </article>
  );
}

function ConnectionInfo({ connection }: { connection: IntegrationConnectionRow }) {
  const day = useDay();
  return (
    <dl className="integration-facts">
      <dt>Connected</dt>
      <dd>{connection.connected_at ? day(connection.connected_at) : "–"}</dd>
      <dt>Last sync</dt>
      <dd>{connection.last_sync_at ? day(connection.last_sync_at) : "Not yet"}</dd>
      {connection.last_error && (
        <>
          <dt>Problem</dt>
          <dd className="danger-text">{connection.last_error}</dd>
        </>
      )}
    </dl>
  );
}

/** Being built: one tap asks to be told when it is live. */
function NotifyMe({ integration, onChanged }: { integration: Integration; onChanged: () => void }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  return (
    <>
      <p className="muted">We're building this now. Tap Notify me and we'll tell you the day it's live.</p>
      <button
        type="button"
        className="primary"
        disabled={busy}
        onClick={() =>
          void run(
            () => api.requestIntegration(integration.key, defaultUseCase(integration.category), null),
            "Thanks — we'll tell you when it's live.",
          ).then((ok) => ok && onChanged())}
      >
        Notify me
      </button>
    </>
  );
}

/** Not built yet: what it should do (required) and anything else (optional), then Send. */
function RequestForm({ integration, onChanged }: { integration: Integration; onChanged: () => void }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [asking, setAsking] = useState(false);
  const [useCase, setUseCase] = useState<UseCase | null>(null);
  const [note, setNote] = useState("");
  if (!asking) {
    return (
      <>
        <p className="muted">Not built yet. Tell us you need it: requests decide what we build next.</p>
        <button type="button" className="primary" onClick={() => setAsking(true)}>
          Request integration support
        </button>
      </>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!useCase) return;
    void run(
      () => api.requestIntegration(integration.key, useCase, note.trim() || null),
      "Thanks — we'll tell you when it's live.",
    ).then((ok) => ok && onChanged());
  };
  return (
    <form className="integration-request" onSubmit={submit}>
      <fieldset>
        <legend>What should it do?</legend>
        <div className="chips">
          {USE_CASES.map((u) => (
            <label key={u.key} className={`chip${useCase === u.key ? " on" : ""}`}>
              <input
                type="radio"
                name="use-case"
                value={u.key}
                checked={useCase === u.key}
                onChange={() => setUseCase(u.key)}
              />
              {u.label}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="field">
        <span>Anything else? (optional)</span>
        <textarea maxLength={200} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        <span className="hint">{200 - note.length} characters left</span>
      </label>
      <div className="actions">
        <button type="button" onClick={() => setAsking(false)}>Cancel</button>
        <button type="submit" className="primary" disabled={!useCase || busy}>Send</button>
      </div>
    </form>
  );
}

/** Already asked by someone in the company: who and when, the notes, and room for one more. */
function Requested(
  { integration, request, building, onChanged }: {
    integration: Integration;
    request: IntegrationRequestRow;
    building: boolean;
    onChanged: () => void;
  },
) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const day = useDay();
  const [note, setNote] = useState("");
  const notes = [
    ...(request.note ? [{ note: request.note, by: request.requested_by, at: request.requested_at }] : []),
    ...request.notes,
  ];
  return (
    <>
      {building
        ? (
          <p>
            <strong>✓ Coming soon</strong> — we'll tell you when it's live.
          </p>
        )
        : (
          <p>
            <strong>✓ Requested</strong> on {day(request.requested_at)}
            {request.requested_by ? ` by ${request.requested_by}` : ""}. We'll tell you when it's live.
          </p>
        )}
      {notes.length > 0 && (
        <ul className="integration-notes">
          {notes.map((n, k) => (
            <li key={k}>
              <span>{n.note}</span> <span className="faint">— {n.by ?? "Someone"}, {day(n.at)}</span>
            </li>
          ))}
        </ul>
      )}
      <form
        className="integration-request"
        onSubmit={(e) => {
          e.preventDefault();
          if (!note.trim()) return;
          void run(() => api.requestIntegration(integration.key, null, note.trim()), "Note added.")
            .then((ok) => {
              if (ok) {
                setNote("");
                onChanged();
              }
            });
        }}
      >
        <label className="field">
          <span>Add a note</span>
          <textarea maxLength={200} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="actions">
          <button type="submit" disabled={!note.trim() || busy}>Add note</button>
        </div>
      </form>
    </>
  );
}

/** IVR numbers typed one per line or with commas. */
function splitNumbers(text: string): string[] {
  return text.split(/[\n,;]+/).map((n) => n.trim()).filter(Boolean);
}

/** Connect MyOperator (D-147): its Public API token and the numbers its IVR rings the team from. */
function MyOperatorConnect({ onChanged, again = false }: { onChanged: () => void; again?: boolean }) {
  const { api } = useSession();
  const { busy, run } = useAction();
  const [token, setToken] = useState("");
  const [numbers, setNumbers] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const done = await api.connectMyOperator(token.trim(), splitNumbers(numbers));
      setToken("");
      if (done.sync?.error) throw new Error(`Connected, but the first read failed: ${done.sync.error}`);
    }, "MyOperator is connected; the last 7 days are matched.").then((ok) => ok && onChanged());
  };
  return (
    <form className="integration-request" onSubmit={submit}>
      <p className="muted">
        {again
          ? "Connect again with a new token."
          : "Connect your MyOperator account, and calls your IVR rings the team with land on the real caller."}
      </p>
      <label className="field">
        <span>Public API token</span>
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <span className="hint">
          In MyOperator: Manage → API integration → Public API token. It is kept encrypted and used only to read your
          call log.
        </span>
      </label>
      <label className="field">
        <span>IVR numbers</span>
        <textarea
          rows={2}
          value={numbers}
          placeholder="011 4000 4999"
          onChange={(e) => setNumbers(e.target.value)}
        />
        <span className="hint">
          The numbers your IVR rings your team from, as the phone shows them: one per line, or with commas. Leave it
          empty and Voxowa finds them in the last 7 days.
        </span>
      </label>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy || token.trim().length < 8}>
          {busy ? "Connecting…" : "Connect"}
        </button>
      </div>
    </form>
  );
}

/** A connected MyOperator: how it is going, its IVR numbers and which telecaller each MyOperator user is. */
function MyOperatorManage({ connection, onChanged }: { connection: IntegrationConnectionRow; onChanged: () => void }) {
  const { api, tz } = useMember();
  const { busy, run } = useAction();
  const members = useLoad(() => api.members(), [api]);
  const settings = connection.settings ?? {};
  const users = settings.users ?? [];
  const [numbers, setNumbers] = useState((settings.ivr_numbers ?? []).join("\n"));
  const [map, setMap] = useState<Record<string, string | null>>(
    Object.fromEntries(users.map((u) => [u.id, u.member_id])),
  );
  const team = (members.data ?? []).filter((m) => m.status === "active");
  const save = () =>
    void run(() => api.updateMyOperatorSettings(splitNumbers(numbers), map), "Saved.").then((ok) => ok && onChanged());
  const sync = () =>
    void run(async () => {
      const done = await api.syncMyOperator();
      if (done.error) throw new Error(done.error);
    }, "MyOperator is read; any new matches are made.").then((ok) => ok && onChanged());
  const disconnect = () =>
    void run(() => api.disconnectIntegration("myoperator"), "MyOperator is disconnected.").then((ok) =>
      ok && onChanged()
    );
  return (
    <div className="stack">
      {connection.status === "error" && (
        <p className="danger-text">{connection.last_error ?? "MyOperator stopped answering."} Connect again below.</p>
      )}
      <dl className="integration-facts">
        <dt>Connected</dt>
        <dd>{connection.connected_at ? fmtDateTime(connection.connected_at, tz) : "–"}</dd>
        <dt>Last read</dt>
        <dd>{connection.last_sync_at ? fmtDateTime(connection.last_sync_at, tz) : "Not yet"}</dd>
        <dt>Calls matched</dt>
        <dd>
          {settings.moved ?? 0} IVR {settings.moved === 1 ? "call" : "calls"} moved to the real caller
          {settings.last_result?.ambiguous
            ? <span className="faint">{` · ${settings.last_result.ambiguous} left alone (two callers at once)`}</span>
            : null}
        </dd>
      </dl>
      <label className="field">
        <span>IVR numbers</span>
        <textarea
          rows={Math.max(2, splitNumbers(numbers).length)}
          value={numbers}
          onChange={(e) => setNumbers(e.target.value)}
        />
        <span className="hint">
          One per line, as the phone shows them. A call from one of these is looked up in MyOperator.
        </span>
      </label>
      {users.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>MyOperator user</th>
                <th>In your team</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.name ?? "Unnamed"} <span className="faint">{u.number ?? ""}</span>
                  </td>
                  <td>
                    <select
                      value={map[u.id] ?? ""}
                      onChange={(e) => setMap({ ...map, [u.id]: e.target.value || null })}
                      aria-label={`Who ${u.name ?? "this user"} is`}
                    >
                      <option value="">Not in Voxowa</option>
                      {team.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="actions">
        <ConfirmButton label="Disconnect" confirm="Disconnect MyOperator?" onConfirm={disconnect} disabled={busy} />
        <button type="button" onClick={sync} disabled={busy}>Read now</button>
        <button type="button" className="primary" onClick={save} disabled={busy}>Save</button>
      </div>
      {connection.status === "error" && <MyOperatorConnect again onChanged={onChanged} />}
    </div>
  );
}
